<script setup lang="ts">
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import { IDropdownSubmenuAction, IDropdownSubmenuActionOption } from '@/interfaces';
import { hasMenuSeparatorBefore } from '@/utils/menu.utils';
import { EPosition } from 'common/enum/misc.enum';
import { computed, ref, toRefs, useId, useTemplateRef } from 'vue';

const emit = defineEmits<{
  'click:action': [];
}>();

const props = defineProps<{
  action: IDropdownSubmenuAction;
  anchorButtonRef?: HTMLElement;
  label?: string;
}>();
const { action, anchorButtonRef } = toRefs(props);

const dialogRef = useTemplateRef('dialogRef');
const contentId = useId();
const expandedGroupIds = ref(new Set<number | string>());
const isPending = ref(false);

const show = (): void => {
  expandedGroupIds.value = new Set(
    groupsResolved.value.filter((group) => group.initiallyExpanded).map((group) => group.id),
  );
  dialogRef.value?.show();
};

const toggleGroup = (id: number | string): void => {
  if (expandedGroupIds.value.has(id)) expandedGroupIds.value.delete(id);
  else expandedGroupIds.value.add(id);
};

type TSubmenuOption = IDropdownSubmenuActionOption<number | string>;

const groupsResolved = computed(() => {
  return action.value.groups
    .map((group, index) => ({
      ...group,
      id: group.id ?? index,
      options: group.options.filter((option) => !option.hidden),
    }))
    .filter((group) => group.options.length > 0);
});
const hasCollapsibleGroups = computed(() => groupsResolved.value.some((group) => group.collapsible));

const handleOptionClick = async (option: TSubmenuOption): Promise<void> => {
  if (option.disabled || isPending.value) return;
  isPending.value = true;
  const runAction = action.value.action;
  try {
    // Close the modal drawer and restore focus before an action navigates or scrolls the page.
    await dialogRef.value?.hideAndWait();
    emit('click:action');
    await runAction(option);
  } finally {
    isPending.value = false;
  }
};

defineExpose({
  show,
});
</script>

<template>
  <PositionablePopover
    ref="dialogRef"
    :anchor-element="anchorButtonRef"
    :position="EPosition.RIGHT"
    :offset-x="4"
    :scroll-mode="hasCollapsibleGroups ? 'content' : 'popover'"
    :mobile-drawer-mode="hasCollapsibleGroups"
    :mobile-drawer-label="props.label"
  >
    <template #default="{ mobileDrawer }">
      <div
        class="flex min-h-0 flex-col"
        :class="[
          hasCollapsibleGroups ? 'overflow-y-auto overscroll-contain' : 'overflow-hidden',
          mobileDrawer ? 'w-full flex-1 p-smd' : 'w-[262px] max-w-[262px] p-sm',
          hasCollapsibleGroups && !mobileDrawer && 'max-h-selection-popover',
        ]"
        @click.stop
        @keydown.enter.stop
        @keydown.space.stop
      >
        <div v-for="(group, groupIndex) of groupsResolved" :key="group.id">
          <CustomSeparator v-if="groupIndex > 0" class="my-sm" />
          <button
            v-if="group.collapsible"
            type="button"
            class="flex w-full min-h-button items-center gap-sm rounded-md p-sm text-left text-secondary font-semibold state-effects"
            :aria-expanded="expandedGroupIds.has(group.id)"
            :aria-controls="`${contentId}-${group.id}`"
            @click="toggleGroup(group.id)"
          >
            <ChevronRightIcon
              class="size-icon-xs shrink-0 transition-transform duration-200"
              :class="expandedGroupIds.has(group.id) && 'rotate-90'"
            />
            <span class="truncate">{{ group.label }}</span>
          </button>
          <span v-else-if="group.label" class="font-semibold text-secondary p-sm block truncate" :title="group.label">
            {{ group.label }}
          </span>
          <div v-if="!group.collapsible || expandedGroupIds.has(group.id)" :id="`${contentId}-${group.id}`">
            <template v-for="(option, optionIndex) of group.options" :key="`${option.value}-${optionIndex}`">
              <CustomSeparator v-if="hasMenuSeparatorBefore(group.options, optionIndex)" class="my-sm" />
              <ClickableListItem
                role="button"
                :aria-disabled="option.disabled || isPending"
                class="p-sm outline-inner flex justify-between min-h-button"
                :class="{
                  'text-error': option.style === 'error',
                  'opacity-50 pointer-events-none': option.disabled,
                }"
                :title="option.label"
                :disabled="option.disabled || isPending"
                @click="handleOptionClick(option)"
              >
                <div class="flex gap-sm flex-1 items-center min-w-0">
                  <AnyIcon v-if="option.iconBefore" :icon-name="option.iconBefore" class="size-icon flex-shrink-0" />
                  <span class="block truncate">{{ option.label }}</span>
                  <component :is="option.iconAfter" v-if="option.iconAfter" class="ml-auto" />
                </div>
              </ClickableListItem>
            </template>
          </div>
        </div>
      </div>
    </template>
  </PositionablePopover>
</template>
